import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Section, StatusBadge } from "@/components/common/kit";
import RemoteTable from "@/components/common/RemoteTable";

const status = { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} testid={`req-status-${r.transfer_no || r.return_no}`} /> };
export const TRANSFER_COLS = [
  { key: "transfer_no", label: "Transfer ID" }, { key: "request_date", label: "Requested", type: "date" },
  { key: "from_name", label: "From" }, { key: "to_name", label: "To" }, { key: "product_name", label: "Product" },
  { key: "qty", label: "Packs", type: "int" }, { key: "remarks", label: "Remarks" }, status,
  { key: "decided_by", label: "Decided by" }, { key: "decided_at", label: "Decided at", type: "datetime" },
];
export const RETURN_COLS = [
  { key: "return_no", label: "Return ID" }, { key: "request_date", label: "Requested", type: "date" },
  { key: "salesman_name", label: "Salesman" }, { key: "product_name", label: "Product" }, { key: "qty", label: "Packs", type: "int" },
  { key: "reason", label: "Reason" }, status, { key: "approved_by", label: "Decided by" }, { key: "decided_at", label: "Decided at", type: "datetime" },
];

const CFG = {
  transfers: { title: "Stock Transfers", sub: "Salesman-to-salesman transfers. Stock moves only after approval.", cols: TRANSFER_COLS, no: "transfer_no" },
  returns: { title: "Stock Returns", sub: "Approved returns are removed from salesman stock and recorded as returned to company.", cols: RETURN_COLS, no: "return_no" },
};

export default function Requests({ kind }) {
  const cfg = CFG[kind];
  const [tab, setTab] = useState("PENDING");
  const [dec, setDec] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [rk, setRk] = useState(0);

  const decide = async () => {
    try {
      await api.post(`/${kind}/${dec.row.id}/decide`, { action: dec.action, remarks });
      toast.success(`${dec.row[cfg.no]} ${dec.action === "approve" ? "approved" : "rejected"}`);
      setDec(null);
      setRemarks("");
      setRk(rk + 1);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const cols = [...cfg.cols, {
    key: "act", label: "", sortable: false, noExport: true,
    render: (r) => r.status === "PENDING" && (
      <div className="flex justify-end gap-1">
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setDec({ row: r, action: "approve" })} data-testid={`approve-${r[cfg.no]}`}>Approve</Button>
        <Button size="sm" variant="outline" className="text-red-600" onClick={() => setDec({ row: r, action: "reject" })} data-testid={`reject-${r[cfg.no]}`}>Reject</Button>
      </div>
    ),
  }];

  return (
    <div className="space-y-6">
      <PageHeader title={cfg.title} subtitle={cfg.sub} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {["PENDING", "APPROVED", "REJECTED", "ALL"].map((s) => <TabsTrigger key={s} value={s} data-testid={`${kind}-tab-${s.toLowerCase()}`}>{s[0] + s.slice(1).toLowerCase()}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <Section>
        <RemoteTable url={`/${kind}`} params={{ status: tab === "ALL" ? undefined : tab }} reloadKey={rk} columns={cols} title={cfg.title} exportable testid={`${kind}-table`} />
      </Section>
      <Dialog open={!!dec} onOpenChange={(o) => !o && setDec(null)}>
        <DialogContent className="max-w-md" data-testid="decide-dialog">
          <DialogHeader><DialogTitle>{dec?.action === "approve" ? "Approve" : "Reject"} {dec?.row[cfg.no]}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            {dec?.row.qty} packs of {dec?.row.product_name}{kind === "transfers" ? ` from ${dec?.row.from_name} to ${dec?.row.to_name}` : ` from ${dec?.row.salesman_name}`}
          </p>
          <Input placeholder="Remarks (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="decide-remarks" />
          <DialogFooter><Button onClick={decide} variant={dec?.action === "approve" ? "default" : "destructive"} data-testid="decide-confirm-button">Confirm {dec?.action}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
